import { saveAs } from 'file-saver';

import { baseApi } from '@klp/core/api/services/rtkApi';
import { FileObject } from '@klp/file/classes';

import type { FileDownloadRequest } from './classes/FileDownloadRequest';

const b64toBlob = (b64Data: any, contentType = '', sliceSize = 512) => {
    const byteCharacters = atob(b64Data);
    const byteArrays: Array<any> = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
};

export const fileDownloadApi = baseApi.injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
        downloadFile: builder.mutation<
            { fileContent?: Blob; filePreviewUrl?: string },
            FileDownloadRequest
        >({
            queryFn: async (params, _api, _extraOptions, baseQuery) => {
                const {
                    filePath,
                    fileName,
                    fileContentField,
                    queryParams,
                    filePreview,
                } = params;

                try {
                    // Fetch file data
                    let result: any;
                    if (fileContentField) {
                        result = await baseQuery({
                            url: filePath,
                            params: queryParams,
                        });

                        if (result.error) {
                            return { error: result.error };
                        }

                        const file = new FileObject(result.data);
                        file.fileContentBlob = b64toBlob(
                            result.data[fileContentField],
                            result.data.type,
                        );

                        if (filePreview) {
                            return {
                                data: {
                                    fileContent: file.fileContentBlob,
                                    filePreviewUrl: URL.createObjectURL(
                                        file.fileContentBlob,
                                    ),
                                },
                            };
                        } else {
                            saveAs(file.fileContentBlob, file.fileName);
                            return { data: {} };
                        }
                    } else {
                        result = await baseQuery({
                            url: filePath,
                            params: queryParams,
                            responseHandler: (response) => response.blob(),
                        });

                        if (result.error) {
                            return { error: result.error };
                        }

                        const blob = result.data as Blob;

                        if (filePreview) {
                            return {
                                data: {
                                    fileContent: blob,
                                    filePreviewUrl: URL.createObjectURL(blob),
                                },
                            };
                        } else {
                            saveAs(blob, fileName);
                            return { data: {} };
                        }
                    }
                } catch (error) {
                    return { error: { status: 'CUSTOM_ERROR', error } };
                }
            },
        }),
    }),
});

export const { useDownloadFileMutation } = fileDownloadApi;
